/**
 * Utility for showing confirmation dialogs
 * Provides a simple API to replace window.confirm()
 */

import { translate } from './i18nHelper'
import { ConfirmOptions, AccountRemovalOptions } from '@/app/types/ui/dialog';

// Re-export types for convenience
export type { ConfirmOptions, AccountRemovalOptions };

// Global state for dialog management
let globalDialogHandler: ((options: ConfirmOptions & { 
  onConfirm: () => void | Promise<void>
  onCancel?: () => void
}) => void) | null = null

/**
 * Register the global dialog handler (called by the app root)
 */
export function registerConfirmDialogHandler(handler: typeof globalDialogHandler): void {
  globalDialogHandler = handler
}

/**
 * Show a generic confirmation dialog
 * Returns a Promise that resolves to true if confirmed, false if cancelled
 */
export function showConfirmDialog(options: ConfirmOptions = {}): Promise<boolean> {
  return new Promise((resolve) => {
    console.log('showConfirmDialog called with options:', options)
    console.log('globalDialogHandler available:', !!globalDialogHandler)
    
    if (!globalDialogHandler) {
      console.warn('Global dialog handler not available, falling back to window.confirm')
      // Fallback to browser confirm if dialog system not available
      const message = options.message || translate('dialog.confirmation.generic.message')
      resolve(window.confirm(message))
      return
    }

    console.log('Using global dialog handler')
    globalDialogHandler({
      ...options,
      onConfirm: () => {
        console.log('Dialog confirmed')
        resolve(true)
      },
      onCancel: () => {
        console.log('Dialog cancelled')
        resolve(false)
      },
    })
  })
}

/**
 * Show account removal confirmation dialog
 * Returns a Promise that resolves to true if confirmed, false if cancelled
 */
export function showAccountRemovalConfirm(options: AccountRemovalOptions): Promise<boolean> {
  const message = translate('dialog.confirmation.removeAccount.message', {
    userName: options.userName,
    email: options.email,
  })

  return showConfirmDialog({
    title: options.title || translate('dialog.confirmation.removeAccount.title'),
    message,
    confirmText: options.confirmText || translate('dialog.confirmation.removeAccount.confirm'),
    cancelText: options.cancelText || translate('dialog.confirmation.removeAccount.cancel'),
    variant: "destructive",
  })
}

/**
 * Simple replacement for window.confirm() with better UX
 * Usage: const confirmed = await confirm("Are you sure?")
 */
export async function confirm(message: string, title?: string): Promise<boolean> {
  return showConfirmDialog({
    title,
    message,
  })
}